// src/pages/News/NewsDetail.tsx

import React, { useEffect, useRef } from 'react';
import { useParams, Link, useNavigate, useLocation } from 'react-router-dom';
import { news_content, NewsItem } from '../../content/news/newsContent';

const NewsDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const commentFormRef = useRef<HTMLDivElement>(null);

  // Find by ID
  const newsItem: NewsItem | undefined = news_content.articles.find(
    (n) => n.id === Number(id)
  );

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  useEffect(() => {
    const query = new URLSearchParams(location.search);

    if (query.get('comment') === '1' && commentFormRef.current) {
      setTimeout(() => {
        commentFormRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
        navigate(location.pathname, { replace: true });
      }, 200);
    }
  }, [location.search, navigate, location.pathname]);

  if (!newsItem) {
    return (
      <div className='max-w-7xl mx-auto py-20 text-center'>
        <h2 className='text-2xl text-red-600'>News article not found!</h2>
        <Link to='/news' className='underline'>
          Back to News
        </Link>
      </div>
    );
  }

  // Split plain text into paragraphs (by double newline or sentence boundaries)
  const formatContent = (text: string) => {
    // Split by double newlines first (if any)
    let paragraphs = text.split(/\n\s*\n/);

    // If no double newlines, split by sentences and group every 2-3 sentences
    if (paragraphs.length === 1) {
      const sentences = text.split(/(?<=[.!?])\s+/);
      paragraphs = [];
      for (let i = 0; i < sentences.length; i += 2) {
        paragraphs.push(sentences.slice(i, i + 2).join(' '));
      }
    }

    return paragraphs.map((para, idx) => (
      <p key={idx} className="mb-4">
        {para}
      </p>
    ));
  };

  return (
    <div className='mx-auto pt-12 pb-20 px-4 sm:px-6 lg:px-8 bg-[#FFFDF8]'>

      <div className='max-w-7xl mx-auto' style={{ paddingTop: '6rem' }}>

        {/* Breadcrumb */}
        <div className='text-md breadcrumbs mb-6'>
          <ul className='flex space-x-2 text-gray-600'>
            <li>
              <Link to='/' className='hover:text-[#021847]'>
                Home
              </Link>
            </li>
            <li>›</li>
            <li>
              <Link to='/news' className='hover:text-[#021847]'>
                News
              </Link>
            </li>
            <li>›</li>
            <li className='text-[#021847]'>{newsItem.headline}</li>
          </ul>
        </div>

        <div className='relative py-10 px-4 sm:px-6 lg:px-8'>
          <div className='absolute top-0 left-0'>
            <h2 className='text-[#021847] text-2xl font-semibold uppercase tracking-widest'>
              NEWS
            </h2>
            <div className='w-12 h-0.5 bg-[#021847] mt-1' />
          </div>
        </div>

        <article className='bg-white rounded-lg shadow-lg overflow-hidden'>
          <img
            src={newsItem.images[0]}
            alt={newsItem.headline}
            className='w-full md:h-[39rem] object-cover'
          />

          <div className='p-8'>
            <h1 className='text-3xl font-bold text-[#021847] mb-4 uppercase'>
              {newsItem.headline}
            </h1>

            <div className='flex gap-4 text-sm text-gray-600 mb-6'>
              <span>{newsItem.date}</span>
              <span>•</span>
              <span>{newsItem.comments} Comments</span>
              <span>•</span>
              <span>{newsItem.likes} Likes</span>
              <span>•</span>
              <span>{newsItem.shares} Shares</span>
            </div>

            {/* FULL CONTENT - Plain text formatted into paragraphs */}
            <div className='text-[#6E6E6E] text-[18px]'>
              {formatContent(newsItem.content)}
            </div>
          </div>
        </article>

      </div>
    </div>
  );
};

export default NewsDetail;